import googleTagManager from '@analytics/google-tag-manager'

import type { AnalyticsPlugin } from 'analytics'
import { AnalyticsServiceSettings } from 'models/AnalyticsServiceSettings'

interface GTMPluginConfig {
  isEmbedded: boolean
  campaignId: string
  organizationId: string
}

interface GTMSettings {
  container_id: string
}

/**
 * Creates GTM plugin with proper configuration
 * !!! --> Only init when not embedded (not in iframe context)
 * (if embedded, GTM events are handled by cross-frame-analytics)
 */
export const makeGTMAnalyticsPlugin = (
  settings: AnalyticsServiceSettings[],
  config: GTMPluginConfig,
): AnalyticsPlugin | undefined => {
  try {
    if (config.isEmbedded) {
      return undefined
    }

    const gtmSettings = getGTMSettings(settings)

    if (!gtmSettings) {
      return undefined
    }

    const gtmPlugin = googleTagManager({ containerId: gtmSettings.container_id })

    // Return GTM plugin (no data flattening needed - GTM accepts nested objects)
    return {
      ...gtmPlugin,
      name: 'google-tag-manager',
    }
  } catch (error) {
    console.error('Failed to initialize GTM plugin:', error)
    return undefined
  }
}

/**
 * Extracts GTM settings from analytics service settings
 */
function getGTMSettings(settings: AnalyticsServiceSettings[]): GTMSettings | null {
  const gtmSetting = settings.find((setting) => setting.service_name === 'google_tag_manager') as
    | AnalyticsServiceSettings<GTMSettings, object>
    | undefined

  if (!gtmSetting?.parameters?.container_id) {
    console.warn('GTM plugin: container_id is required but not provided')
    return null
  }

  if (!gtmSetting.parameters.container_id.startsWith('GTM-')) {
    console.warn('GTM plugin: container_id should start with "GTM-"')
    return null
  }

  return {
    container_id: gtmSetting.parameters.container_id,
  }
}
